import type { DayTrade } from '@/models/day-trading-session';
import type { DayTradingParticipant } from '@/models/day-trading-participant';
import { calculateDayTrade } from '@/utils/daytrade-calculations';

export const SESSION_BUDGET = 50_000; // 50k USD

export function calculateSessionProfit(trades: DayTrade[], overrideBudget?: number): { profit: number; profitPct: number; budget: number; invested: number } {
  const budget = overrideBudget !== undefined ? overrideBudget : SESSION_BUDGET;
  let totalProfit = 0;
  let totalInvested = 0;

  for (const trade of trades) {
    if (trade.state === 'OPEN') continue;
    if (trade.state === 'STOPPED') continue;

    const calc = calculateDayTrade({
      trade: trade,
      budget: budget * 100, // Convert to cents
      mode: 'summary',
      skippedAdjustements: [],
      overrideAdjustementPrices: {},
      overrideAdjustmentStockCount: {},
      stockCountMode: 'NORMAL',
      processedAdjustements: [],
    });

    totalProfit += calc.profit;
    totalInvested += calc.totalInvested;
  }

  const profitPct = budget > 0 ? (totalProfit / budget) * 100 : 0;
  return { profit: totalProfit, profitPct: profitPct, budget: budget, invested: totalInvested };
}

export function calculatePersonalSessionProfit(
  trades: DayTrade[],
  participant: DayTradingParticipant | null,
  overrideBudget?: number,
): { profit: number; profitPct: number; budget: number; invested: number } | null {
  if (participant === null) return null;
  const budgetCents = overrideBudget !== undefined ? overrideBudget * 100 : participant.state.budget;
  if (budgetCents <= 0) return null;

  const processed = participant.state.processedAdjustements ?? [];
  const skipped = participant.state.skippedAdjustements ?? [];
  if (processed.length === 0) return null;

  let totalProfit = 0;
  let totalInvested = 0;
  let anyTradeCounted = false;

  for (const trade of trades) {
    if (trade.state === 'OPEN') continue;
    if (trade.state === 'STOPPED') continue;

    const tradeAdjIds = new Set(trade.adjustments.map((a) => a.id));
    const userTouchedThisTrade = processed.some((id) => tradeAdjIds.has(id));
    if (!userTouchedThisTrade) continue;

    anyTradeCounted = true;

    const calc = calculateDayTrade({
      trade: trade,
      budget: budgetCents,
      mode: 'summary',
      processedAdjustements: processed,
      skippedAdjustements: skipped,
      overrideAdjustementPrices: participant.state.overrideAdjustementPrices ?? {},
      overrideAdjustmentStockCount: participant.state.overrideAdjustmentStockCount ?? {},
      stockCountMode: 'NORMAL',
    });

    totalProfit += calc.profit;
    totalInvested += calc.totalInvested;
  }

  if (!anyTradeCounted) return null;

  const budgetUSD = budgetCents / 100;
  const profitPct = (totalProfit / budgetUSD) * 100;
  return { profit: totalProfit, profitPct: profitPct, budget: budgetUSD, invested: totalInvested };
}

export function formatProfitPct(pct: number): string {
  return pct.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function getProfitColorClass(value: number): string {
  if (value > 0) return 'text-green-600 dark:text-green-400';
  if (value < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-500 dark:text-gray-400';
}
